import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiRequest, ApiError } from '../lib/api';
import { useApi } from '../lib/useApi';
import { COUNTIES_BY_USE } from '../lib/counties';
import { ExploreTabs } from '../components/ExploreTabs';
import { PostingGate } from '../components/PostingGate';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import {
  Alert, Button, Card, Field, LoadingBlock, Select, TextInput,
} from '../components/ui';
import '../styles/public.css';

/**
 * List your business (specification §6).
 *
 * Free, and open to anyone who signs in: no payment, no subscription, no
 * approval to join. §46 puts free registration and free basic posting among the
 * must-haves, and the reason is the obvious one — a directory nobody can join
 * is not a directory. §11 asks for the account, which is why PostingGate wraps
 * the form rather than the server refusing it after nine fields.
 *
 * What the form CANNOT do is put a business live. It arrives as pending and an
 * administrator decides; the database refuses anything else regardless of what
 * is posted. That is worth telling the submitter plainly rather than leaving
 * them to wonder why they cannot find themselves afterwards.
 */

interface Category { id: string; slug: string; name: string }

export function ServiceRegister() {
  const { pathname } = useLocation();
  const cats = useApi<{ categories: Category[] }>('/api/v1/public/services/categories');

  const [form, setForm] = useState({
    businessName: '', categoryId: '', phone: '', county: '', town: '',
    contactName: '', whatsapp: '', email: '', description: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      // Empty optionals are omitted rather than sent as '': the schema is
      // strict about types, and '' is not a valid email.
      const body: Record<string, string> = {
        businessName: form.businessName.trim(),
        categoryId: form.categoryId,
        phone: form.phone.trim(),
        county: form.county.trim(),
      };
      for (const k of ['town', 'contactName', 'whatsapp', 'email', 'description'] as const) {
        const v = form[k].trim();
        if (v) body[k] = v;
      }
      const r = await apiRequest<{ provider: { business_name: string } }>(
        '/api/v1/services/register', { method: 'POST', body });
      setDone(r.provider.business_name);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.status === 429
            ? 'That is several registrations in a short time. Please try again later.'
            : err.message)
          : 'That could not be sent. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <PublicHeader />
        <main className="page public-page">
          <ExploreTabs path={pathname} />
          <Card title="Thank you">
            <p style={{ marginTop: 0 }}>
              <strong>{done}</strong> has been sent for review.
            </p>
            <p>
              Somebody at NyumbaSuite checks every business before it appears in the
              directory, so it will not be visible straight away. We will be in touch
              on the number you gave us if we need anything.
            </p>
            <Link className="btn btn-primary" to="/services">Back to services</Link>
          </Card>
        </main>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <PublicHeader />
      <main className="page public-page">
        <ExploreTabs path={pathname} />

        <div className="page-head">
          <div>
            <h1 className="page-title">List your business, free</h1>
            <p className="page-sub">
              Be found by landlords, tenants and homeowners across Kenya. There is no charge.
            </p>
          </div>
        </div>

        <PostingGate what="list your business">
        {cats.loading ? <LoadingBlock /> : (
          <form onSubmit={submit} style={{ maxWidth: 720 }}>
            {error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></div>}

            <Card title="About the business">
              <div className="grid grid-2">
                <Field label="Business name" htmlFor="r-name">
                  <TextInput id="r-name" required value={form.businessName}
                    onChange={set('businessName')} placeholder="Juma Plumbing Works" />
                </Field>
                <Field label="Service" htmlFor="r-cat">
                  <Select id="r-cat" required value={form.categoryId} onChange={set('categoryId')}>
                    <option value="">Choose a service…</option>
                    {(cats.data?.categories ?? []).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="County" htmlFor="r-county">
                  {/* The same list the directory filters by, so a business is
                      findable under the county it chose. */}
                  <Select id="r-county" required value={form.county} onChange={set('county')}>
                    <option value="">Choose a county…</option>
                    {COUNTIES_BY_USE.map(c => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </Field>
                <Field label="Town or area (optional)" htmlFor="r-town">
                  <TextInput id="r-town" value={form.town} onChange={set('town')}
                    placeholder="Westlands" />
                </Field>
              </div>
              <Field label="What you do (optional)" htmlFor="r-desc">
                <textarea id="r-desc" className="textarea" rows={4} maxLength={2000}
                  value={form.description} onChange={set('description')}
                  placeholder="Taps, drains, water tanks, bathroom fittings. Same-day callouts in Nairobi." />
              </Field>
            </Card>

            <Card title="How people reach you">
              <p className="muted" style={{ marginTop: 0 }}>
                Your phone number is shown publicly — that is the point of the directory.
              </p>
              <div className="grid grid-2">
                <Field label="Phone" htmlFor="r-phone">
                  <TextInput id="r-phone" required value={form.phone}
                    onChange={set('phone')} placeholder="+254 7xx xxx xxx" />
                </Field>
                <Field label="WhatsApp (optional)" htmlFor="r-wa">
                  <TextInput id="r-wa" value={form.whatsapp} onChange={set('whatsapp')}
                    placeholder="+254 7xx xxx xxx" />
                </Field>
                <Field label="Contact name (optional)" htmlFor="r-contact">
                  <TextInput id="r-contact" value={form.contactName}
                    onChange={set('contactName')} placeholder="Juma Otieno" />
                </Field>
                <Field label="Email (optional)" htmlFor="r-email">
                  <TextInput id="r-email" type="email" value={form.email}
                    onChange={set('email')} placeholder="you@example.com" />
                </Field>
              </div>
            </Card>

            <div className="row" style={{ gap: 12, alignItems: 'center', marginTop: 16 }}>
              <Button type="submit" loading={busy}>Send for review</Button>
              <p className="muted" style={{ margin: 0 }}>
                We check every business before it appears.
              </p>
            </div>
          </form>
        )}
        </PostingGate>
      </main>
      <SiteFooter />
    </>
  );
}
